/* eslint-disable css-modules/no-unused-class, camelcase, no-nested-ternary  */
import { FC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectIsPLG, selectIsTournamentsGamePackageEnabled, selectToken } from '@redux/slices/userSlice';
import { useGetTournamentUsersLeaderboard } from '@queries/tournaments/tournament';
import { SmallLoader } from 'learning-panel-components';
import { translate } from 'translations';
import { useGetUsersLeaderboards } from '@queries/core/homepage';
import { coreService } from 'network-utils';
import UserLeaderboard, { UserData } from './userLeaderboard';
import styles from './userLeaderboard.module.scss';
import { useGetCoreTournamentUsersLeaderboard } from '../../../../queries/core/tournaments';

const UserLeaderboardContainer: FC<{ searchString: string; isTournament: boolean; tournamentUuid?: string }> = ({
    searchString,
    isTournament,
    tournamentUuid
}: {
    searchString: string;
    isTournament: boolean;
    tournamentUuid?: string;
}) => {
    const token = useSelector(selectToken);
    const isPLG = useSelector(selectIsPLG);
    const limit = 20;
    const [savedUsers, setSavedUsers] = useState<UserData[]>([]);
    const [savedUsersTournament, setSavedUsersTournament] = useState<UserData[]>([]);
    const [savedUsersIdsTournament, setSavedUsersUuidsTournament] = useState<number[]>([]);
    const [currentUser, setCurrentUser] = useState<UserData | null>(null);
    const [offset, setOffset] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);
    const [isEmptyState, setIsEmptyState] = useState<boolean>(false);

    const isTournamentPackageEnabled = useSelector(selectIsTournamentsGamePackageEnabled);

    const { isLoading: usersLeaderboardIsLoading, data: usersLeaderboardsData } = useGetUsersLeaderboards(token, {
        search: searchString,
        offset,
        limit,
        enableQuery: !isPLG && !isTournament
    });

    const { data: tournamentData, isLoading: tournamentLoading } = useGetTournamentUsersLeaderboard(
        token,
        isTournament && !isTournamentPackageEnabled,
        offset,
        limit,
        searchString,
        tournamentUuid
    );

    const { data: tournamentCoreData, isLoading: tournamentCoreLoading } = useGetCoreTournamentUsersLeaderboard({
        token,
        enableQuery: isTournament && isTournamentPackageEnabled,
        offset,
        limit,
        searchString,
        tournamentUuid
    });

    useEffect(() => {
        if (!usersLeaderboardsData) return;
        if ((usersLeaderboardsData as any).success === false) return;
        const tempCurrentUser: UserData = {
            rank: usersLeaderboardsData.currentUser.rank,
            nickname: usersLeaderboardsData.currentUser.name,
            score: usersLeaderboardsData.currentUser.totalScore,
            profilePicture: usersLeaderboardsData.currentUser.profilePicture
        };
        setCurrentUser({ ...tempCurrentUser });
        const users = usersLeaderboardsData.results.map((user: coreService.HomeUserLeaderboard) => {
            return {
                rank: user.rank,
                nickname: user.name,
                score: user.totalScore,
                profilePicture: user.profilePicture
            };
        });
        setSavedUsers(users);
        setTotal(usersLeaderboardsData.total);
    }, [usersLeaderboardsData]);

    const handleTournamentData = useCallback(
        (data: any) => {
            if (!data?.currentUser) return;
            const { currentUser, leaderboard, totalSize } = data;
            const tempCurrentUser: UserData = {
                rank: currentUser.rank,
                nickname: currentUser.nickname,
                score: currentUser.score,
                avatar: currentUser.avatar
            };
            setCurrentUser({ ...tempCurrentUser });
            setTotal(totalSize);

            const tempUsers: UserData[] = offset === 0 ? [] : [...savedUsersTournament];
            const tempUsersIds = offset === 0 ? [] : [...savedUsersIdsTournament];
            leaderboard.length === 0 && searchString !== '' && setIsEmptyState(true);
            leaderboard.map((user: any) => {
                setIsEmptyState(false);
                if (!tempUsersIds.includes(user.userId)) {
                    tempUsersIds.push(user.userId);
                    tempUsers.push({
                        rank: user.rank,
                        nickname: user.nickname,
                        score: user.score,
                        avatar: user.avatar
                    });
                }
            });
            setSavedUsersTournament([...tempUsers]);
            setSavedUsersUuidsTournament([...tempUsersIds]);
        },
        [offset, savedUsersIdsTournament, savedUsersTournament, searchString]
    );

    useEffect(() => {
        if (tournamentData) handleTournamentData(tournamentData);
    }, [tournamentData, handleTournamentData]);

    useEffect(() => {
        if (tournamentCoreData) handleTournamentData(tournamentCoreData);
    }, [tournamentCoreData, handleTournamentData]);

    useEffect(() => {
        if (searchString !== '') setOffset(0);
    }, [searchString]);

    const onLoadMore = () => {
        setOffset(offset + limit);
    };

    return isEmptyState ? (
        <div className={styles['empty-state']}>
            <div className={styles.title}>{translate('noMatchingResults')}</div>
            <div className={styles['sub-title']}>{translate('TryUsingDifferentSearchWord')}</div>
        </div>
    ) : usersLeaderboardIsLoading ||
      tournamentLoading ||
      tournamentCoreLoading ||
      (savedUsers.length === 0 && savedUsersTournament.length === 0) ? (
        <SmallLoader size="100px" />
    ) : (
        currentUser && (
            <UserLeaderboard
                currentUser={currentUser}
                users={isTournament ? savedUsersTournament : savedUsers}
                onLoadMore={onLoadMore}
                hasMore={offset + limit < total}
                isTournament={isTournament}
            />
        )
    );
};

export default UserLeaderboardContainer;
